import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import en from './en';
import hu from './hu';

// Lightweight i18n for Outfino Insights — a locale context + a flat
// key→string dictionary per language (EN/HU). No external i18n dependency:
// simple key lookups with optional {var} fills, mirroring the admin panel.

const DICTS = { en, hu };

const STORAGE_KEY = 'portal_lang';

// Hungarian by default, regardless of the browser. English is picked with the
// dashboard's language switch, and remembered, like the admin's.
function readInitialLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'hu') return saved;
  } catch {
    // Storage blocked — stay on the default.
  }
  return 'hu';
}

const LocaleContext = createContext({
  lang: 'hu',
  setLang: () => {},
  t: (key) => key,
});

export function LocaleProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);

  const setLang = useCallback((next) => {
    const value = next === 'en' ? 'en' : 'hu';
    setLangState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* not remembered — see readInitialLang */
    }
  }, []);

  // t(key[, vars]) → translated string. Falls back to HU, then to the raw key,
  // so a missing translation degrades visibly rather than crashing.
  const t = useCallback((key, vars) => {
    let s = DICTS[lang]?.[key] ?? DICTS.hu[key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return s;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
